import { supabase } from "@/integrations/supabase/client";
import { storageQuotaErrorFrom } from "@/domains/documents/services/buildingStorageQuota";
import {
  copyDocumentsToProject,
  moveDocumentsToProject,
} from "@/stores/documents/repository/mutations";

interface CrossProjectOperationParams {
  documentIds: string[];
  sourceProjectId: string;
  targetProjectId: string;
  targetBuildingId: string;
  orgId: string;
  userId: string;
  targetFolderId?: string | null;
}

interface CrossProjectFolderParams {
  sourceFolderId: string;
  sourceProjectId: string;
  sourceBuildingId: string;
  targetProjectId: string;
  targetBuildingId: string;
  targetParentFolderId: string | null;
  orgId: string;
  userId: string;
}

export const projectCopyService = {
  /**
   * Copy documents to another project (or same project different folder)
   * For same-project copy, pass sourceProjectId to ensure doc stays at original location too
   * Returns detailed results including any failed document IDs
   */
  async copyDocumentsToOtherProject({
    documentIds,
    sourceProjectId,
    targetProjectId,
    targetBuildingId,
    orgId,
    userId,
    targetFolderId,
  }: Partial<CrossProjectOperationParams> & {
    documentIds: string[];
    targetProjectId: string;
    targetBuildingId: string;
    orgId: string;
    userId: string;
    targetFolderId?: string | null;
  }): Promise<{
    copied: number;
    failed: number;
    failedIds: string[];
    attempted: number;
    quotaFailed: number;
    quotaFailureMessage: string | null;
    permissionDenied: number;
  }> {
    const result = await copyDocumentsToProject(
      documentIds,
      targetProjectId,
      targetBuildingId,
      orgId,
      userId,
      targetFolderId,
      sourceProjectId // Pass source for same-project copy handling
    );
    return {
      copied: result.copied,
      failed: result.failed,
      failedIds: result.failedIds,
      attempted: result.attempted,
      quotaFailed: result.quotaFailed,
      quotaFailureMessage: result.quotaFailureMessage,
      permissionDenied: result.permissionDenied,
    };
  },

  /**
   * Move documents to another project (cross-project move)
   */
  async moveDocumentsToOtherProject({
    documentIds,
    sourceProjectId,
    targetProjectId,
    targetBuildingId,
    orgId,
    userId,
    targetFolderId,
  }: CrossProjectOperationParams): Promise<{ moved: number }> {
    const result = await moveDocumentsToProject(
      documentIds,
      sourceProjectId,
      targetProjectId,
      targetBuildingId,
      orgId,
      userId,
      targetFolderId
    );
    return { moved: result.moved };
  },

  /**
   * Copy a folder and all its documents to another project (recursive)
   * Uses copy_folder RPC to handle sibling uniqueness properly
   * Returns detailed results including any failed document copies
   */
  async copyFolderToOtherProject({
    sourceFolderId,
    sourceProjectId,
    sourceBuildingId,
    targetProjectId,
    targetBuildingId,
    targetParentFolderId,
    orgId,
    userId,
  }: CrossProjectFolderParams): Promise<{
    copiedDocs: number;
    copiedFolders: number;
    failedDocs: number;
    failedDocIds: string[];
    quotaFailedDocs: number;
    quotaFailureMessage: string | null;
  }> {
    // Use RPC to create folder with proper uniqueness handling
    const { data: newFolderResult, error: createError } = await supabase.rpc("copy_folder", {
      p_source_folder_id: Number(sourceFolderId),
      p_target_project_id: Number(targetProjectId),
      p_target_building_id: Number(targetBuildingId),
      p_target_parent_folder_id: targetParentFolderId ? Number(targetParentFolderId) : null,
      p_org_id: orgId,
      p_created_by: Number(userId),
    });

    if (createError) throw storageQuotaErrorFrom(createError) ?? createError;

    const newFolderId =
      newFolderResult &&
      typeof newFolderResult === "object" &&
      !Array.isArray(newFolderResult) &&
      "folder_id" in newFolderResult
        ? newFolderResult.folder_id
        : null;
    if (typeof newFolderId !== "number" || !Number.isSafeInteger(newFolderId) || newFolderId <= 0) {
      throw new Error("Invalid folder copy response");
    }
    let copiedDocs = 0;
    let copiedFolders = 1;
    let failedDocs = 0;
    let failedDocIds: string[] = [];
    let quotaFailedDocs = 0;
    let quotaFailureMessage: string | null = null;

    // Get documents in source folder. Filter to latest-version rows only —
    // copy_document duplicates the entire version_group internally, so passing
    // every version row would multiply the copies.
    const { data: folderDocs, error: folderDocsError } = await supabase
      .from("documents")
      .select("id")
      .eq("folder_id", Number(sourceFolderId))
      .eq("is_deleted", false)
      .eq("is_latest_version", true);

    if (folderDocsError) throw folderDocsError;

    // Copy documents using the copy_document RPC (preserves state + tags + versions)
    if (folderDocs && folderDocs.length > 0) {
      const docIds = folderDocs.map((d: { id: number }) => String(d.id));
      const copyResult = await copyDocumentsToProject(
        docIds,
        targetProjectId,
        targetBuildingId,
        orgId,
        userId,
        String(newFolderId)
      );
      copiedDocs += copyResult.copied;
      failedDocs += copyResult.failed;
      failedDocIds = failedDocIds.concat(copyResult.failedIds);
      quotaFailedDocs += copyResult.quotaFailed;
      quotaFailureMessage ??= copyResult.quotaFailureMessage;
    }

    // Recursively copy child folders
    const { data: childFolders, error: childError } = await supabase
      .from("document_folders")
      .select("id")
      .eq("parent_id", Number(sourceFolderId))
      .eq("project_id", Number(sourceProjectId))
      .eq("is_deleted", false);

    if (childError) throw childError;

    for (const child of childFolders || []) {
      const childResult = await this.copyFolderToOtherProject({
        sourceFolderId: String(child.id),
        sourceProjectId,
        sourceBuildingId,
        targetProjectId,
        targetBuildingId,
        targetParentFolderId: String(newFolderId), // New folder becomes parent
        orgId,
        userId,
      });
      copiedDocs += childResult.copiedDocs;
      copiedFolders += childResult.copiedFolders;
      failedDocs += childResult.failedDocs;
      failedDocIds = failedDocIds.concat(childResult.failedDocIds);
      quotaFailedDocs += childResult.quotaFailedDocs;
      quotaFailureMessage ??= childResult.quotaFailureMessage;
    }

    return {
      copiedDocs,
      copiedFolders,
      failedDocs,
      failedDocIds,
      quotaFailedDocs,
      quotaFailureMessage,
    };
  },

  /**
   * Move a folder and all its documents to another project (recursive)
   * Uses copy_folder RPC to handle sibling uniqueness properly
   * Note: moveDocumentsToProject throws on error, so no partial failure tracking needed
   */
  async moveFolderToOtherProject({
    sourceFolderId,
    sourceProjectId,
    sourceBuildingId,
    targetProjectId,
    targetBuildingId,
    targetParentFolderId,
    orgId,
    userId,
  }: CrossProjectFolderParams): Promise<{ movedDocs: number; movedFolders: number }> {
    // Use RPC to create folder with proper uniqueness handling
    const { data: newFolderResult, error: createError } = await supabase.rpc("copy_folder", {
      p_source_folder_id: Number(sourceFolderId),
      p_target_project_id: Number(targetProjectId),
      p_target_building_id: Number(targetBuildingId),
      p_target_parent_folder_id: targetParentFolderId ? Number(targetParentFolderId) : null,
      p_org_id: orgId,
      p_created_by: Number(userId),
    });

    if (createError) throw createError;

    const newFolderId = (newFolderResult as { folder_id: number }).folder_id;
    let movedDocs = 0;
    let movedFolders = 1;

    // Get documents in source folder
    const { data: folderDocs, error: folderDocsError } = await supabase
      .from("documents")
      .select("id")
      .eq("folder_id", Number(sourceFolderId))
      .eq("is_deleted", false);

    if (folderDocsError) throw folderDocsError;

    // Move documents to the new folder
    // Note: moveDocumentsToProject throws on error, ensuring atomicity
    if (folderDocs && folderDocs.length > 0) {
      const docIds = folderDocs.map((d: { id: number }) => String(d.id));
      const moveResult = await moveDocumentsToProject(
        docIds,
        sourceProjectId,
        targetProjectId,
        targetBuildingId,
        orgId,
        userId,
        String(newFolderId)
      );
      movedDocs += moveResult.moved;
    }

    // Get child folders and recursively move
    const { data: childFolders, error: childError } = await supabase
      .from("document_folders")
      .select("id")
      .eq("parent_id", Number(sourceFolderId))
      .eq("project_id", Number(sourceProjectId))
      .eq("is_deleted", false);

    if (childError) throw childError;

    for (const child of childFolders || []) {
      const childResult = await this.moveFolderToOtherProject({
        sourceFolderId: String(child.id),
        sourceProjectId,
        sourceBuildingId,
        targetProjectId,
        targetBuildingId,
        targetParentFolderId: String(newFolderId),
        orgId,
        userId,
      });
      movedDocs += childResult.movedDocs;
      movedFolders += childResult.movedFolders;
    }

    // Delete the source folder after moving its contents
    const { error: deleteError } = await supabase
      .from("document_folders")
      .delete()
      .eq("id", Number(sourceFolderId));

    if (deleteError) throw deleteError;

    return { movedDocs, movedFolders };
  },

  // Get folder path for a given folder ID
  async getFolderPath(folderId: string, buildingId: string, projectId: string): Promise<string> {
    const { data: folder, error } = await supabase
      .from("document_folders")
      .select("name, parent_id, folder_path")
      .eq("id", Number(folderId))
      .eq("building_id", Number(buildingId))
      .eq("project_id", Number(projectId))
      .eq("is_deleted", false)
      .single();

    if (error) throw error;
    return folder.folder_path || folder.name;
  },

  // Get all documents mapped to a folder and its subfolders
  async getDocumentsInFolderSubtree(
    buildingId: string,
    projectId: string,
    folderId: string
  ): Promise<string[]> {
    // First get all subfolders
    const { data: folders, error: foldersError } = await supabase
      .from("document_folders")
      .select("id")
      .eq("building_id", Number(buildingId))
      .eq("project_id", Number(projectId))
      .eq("is_deleted", false)
      .or(`id.eq.${folderId},folder_path.like.%/${folderId}/%`);

    if (foldersError) throw foldersError;

    const folderIds = folders.map((f: { id: number }) => f.id);

    // Get all documents in these folders
    const { data: folderDocs, error: folderDocsError } = await supabase
      .from("documents")
      .select("id")
      .in("folder_id", folderIds)
      .eq("is_deleted", false);

    if (folderDocsError) throw folderDocsError;

    return folderDocs.map((d: { id: number }) => String(d.id));
  },
};
